import React, { FC } from 'react'

// Constants
import { localStorageKeys } from '@sketch/constants'

// Utils
import { getParsedItem, clamp } from '@sketch/utils'

// Hooks
import useUserProfile from 'modules/user/useUserProfile'
import { useWorkspaceSettings } from 'modules/workspace/operations'

// Components
import BannerCardList, {
  Wrapper as SkeletonContainer,
} from 'modules/shares/components/BannerCardList'
import { BannerCardSkeleton } from 'components/BannerCard'

import {
  verifyEmailBanner,
  documentationBanner,
  uploadDocumentsBanner,
  downloadLatestAppBanner,
  collaborationBanner,
  subscribeBanner,
  upgradePersonalWorkspaceBanner,
  addEditorsBanner,
  mfaBanner,
  educationBanner,
  Banner,
} from '../BannerCardList/Banners'

// Types
import { WorkspaceMinimalFragment } from '@sketch/gql-types'

interface EmptyStateWithBannersProps {
  workspace: WorkspaceMinimalFragment
}

// Helpers
const OPTIONAL_BANNERS: Banner[] = [
  documentationBanner(),
  downloadLatestAppBanner(),
]

/**
 * EmptyStateWithBanners
 *
 * Empty state with a list of banner cards. Also, prevents the VerifyBanner
 * (controlled by the UserContext) to be displayed while this component is
 * rendered.
 */
const EmptyStateWithBanners: FC<EmptyStateWithBannersProps> = ({
  workspace,
}) => {
  const { data: userProfile, loading: isLoadingUserProfile } = useUserProfile()
  const {
    data: workspaceSettings,
    loading: isLoadingWorkspaceSettings,
  } = useWorkspaceSettings(workspace)

  const email = userProfile?.me?.email
  const isVerified = userProfile?.me?.isVerified
  const isMfaEnabled = userProfile?.me?.mfaState === 'ENABLED'

  const isAdmin = workspace?.userRole === 'ADMIN'
  const isEditor = workspace?.userCanEdit
  const hasSubscription = !!workspace.customer
  const isEducationWorkspace = workspace.type === 'STUDENT'
  const isPersonalWorkspace = workspace.type === 'PERSONAL'

  const isSsoWorkspace = isLoadingWorkspaceSettings || !!workspaceSettings.sso

  const bannerCardList: Banner[] = []

  if (isPersonalWorkspace) {
    bannerCardList.push(
      uploadDocumentsBanner(),
      upgradePersonalWorkspaceBanner()
    )
  } else {
    // Verify Email banner stays always in the first position
    if (!isVerified) {
      bannerCardList.unshift(verifyEmailBanner({ email: email! }))
    }

    if (!isEducationWorkspace) {
      bannerCardList.push(educationBanner())
    }

    // Mfa Banner to encourage the user to enable it
    if (!isMfaEnabled && isVerified && !isSsoWorkspace) {
      const now = new Date()
      const stopRenderAfter = getParsedItem(localStorageKeys.mfaBanner)

      if (!(now > new Date(stopRenderAfter))) {
        bannerCardList.push(mfaBanner())
      }
    }

    if (isAdmin || isEditor) {
      bannerCardList.push(uploadDocumentsBanner())
    }

    if (isAdmin) {
      // For the subscription we can have 3 variations of banners
      // 1. Subscribe to Sketch
      // 2. Start Collaborating
      // 3. Ready to Add X Editors
      if (hasSubscription) {
        const availableSeats =
          workspaceSettings.workspace?.customer?.billing?.seats.availableSeats
        if (availableSeats) {
          bannerCardList.push(addEditorsBanner({ workspaceSettings }))
        } else {
          bannerCardList.push(collaborationBanner())
        }
      } else {
        bannerCardList.push(subscribeBanner())
      }
    }
  }

  if (isLoadingUserProfile || isLoadingWorkspaceSettings) {
    const totalBanners = bannerCardList.length + OPTIONAL_BANNERS.length

    return (
      <SkeletonContainer aria-busy>
        {Array(clamp(totalBanners, 1, 4))
          .fill('')
          .map((_, index) => (
            <BannerCardSkeleton key={index} />
          ))}
      </SkeletonContainer>
    )
  }

  return (
    <BannerCardList
      maxBanners={4}
      banners={[...bannerCardList, ...OPTIONAL_BANNERS]}
      nonDismissable
    />
  )
}

export default EmptyStateWithBanners
